import React, { EventHandler, SyntheticEvent, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  makeStyles,
  Theme,
  Typography,
  useTheme,
  ChevronRight as ChevronRightIcon,
} from '@quillbot/design-system';
import { useSelector } from 'react-redux';
import Track from 'src/authentication/handlers/track';
import { RootState } from 'src/_common/models/redux/store';
import { extensionLinks } from 'config/constants';
import { getQueryParams } from '@Common/handlers/helper';
import Img from '@Common/components/img';
import { getConfig } from 'next';
const { publicRuntimeConfig } = getConfig();

const useStyles = makeStyles((theme) => ({
  question: {
    fontWeight: 'bold',
    fontSize: '22px',
    margin: '0px',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
      fontSize: '18px',
      width: '100%',
    },
  },

  welcomeBoxHolder: {
    backgroundColor: theme.specialColors.CHECKOUT_PAGE_BG,
    padding: '16px 0px',
    margin: 'auto',
    marginTop: '40px',
    position: 'relative',
    borderRadius: '5px',
    maxWidth: '960px',
    height: '70vh',
    minHeight: '450px',
    maxHeight: '570px',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down(650)]: {
      padding: '16px 12px',
      maxWidth: '100%',
      height: 'auto',
    },
  },
  extBox: {
    padding: '7px 10px',
    margin: '0px 4px',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('lg')]: {
      fontSize: '12px',
      minWidth: '150px',
      margin: '5px 5px',
      width: '100%',
    },
    '& Img': {
      marginRight: '5px',
      maxWidth: '25px',
    },
  },

  mainGrid: {
    maxWidth: '1366px',
    margin: 'auto',
  },

  extBoxHolder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '16px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      maxWidth: '171px',
      margin: 'auto',
      marginTop: '20px',
    },
  },
  welcomeBox: {
    padding: '16px 100px 26px 120px',
    flex: 1,
    [theme.breakpoints.down(650)]: {
      padding: '16px 12px',
      flexDirection: 'column-reverse',
    },
  },

  continueBtn: {
    justifyContent: 'flex-end',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center',
    },
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  extText: {
    marginTop: '72px',
    [theme.breakpoints.down(650)]: {
      textAlign: 'center',
      marginTop: '52px',
    },
  },
  congratsText: {
    [theme.breakpoints.down(650)]: {
      textAlign: 'center',
      marginTop: '12px',
    },
  },
  quilly: {
    maxWidth: '100%',
  },
  textBox: {
    // [theme.breakpoints.up(650)]: {
    //   paddingTop: '40px',
    //   paddingBottom: '16px',
    // },
  },
}));
function Welcome(props: { onNext: EventHandler<SyntheticEvent> }) {
  const classes = useStyles(props);
  const userName = useSelector((state: RootState) => state.user?.fullName);
  const platform = useSelector((state: RootState) => state.common?.platform);
  const theme = useTheme<Theme>();
  useEffect(() => {
    Track.shownSection('welcome');
    window.scrollTo(0, 0);
  }, []);

  const selectClickHandler = (value: 'chrome' | 'docs' | 'word') => {
    Track.extensionLinkClicked(value, 'welcomePage');

    if (value === 'chrome') {
      window.open(
        `${extensionLinks.chrome}${getQueryParams('welcomePage')}`,
        '_blank'
      );
    } else window.open(extensionLinks[value], '_blank');
  };

  return (
    <>
      <Grid container alignItems="center" className={classes.mainGrid}>
        <Grid item xs={12}>
          <Box boxShadow={3} className={classes.welcomeBoxHolder}>
            <Grid container alignItems="center" className={classes.welcomeBox}>
              <Grid
                item
                container
                xs={platform.isMobile ? 12 : 8}
                className={classes.textBox}
              >
                <Grid item container className={classes.congratsText}>
                  <Typography classes={{ body1: classes.question }}>
                    You’re all set, {userName ? `${userName}!` : ''}
                  </Typography>

                  <Typography style={{ margin: '0px', maxWidth: '320px' }}>
                    Join over 10 million users enjoying{' '}
                    <Typography
                      component="span"
                      style={{
                        fontWeight: 600,
                      }}
                    >
                      Unlimited Paraphrasing.
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item container className={classes.extText}>
                  <Grid item style={{ maxWidth: '330px' }}>
                    <Typography classes={{ body1: classes.question }}>
                      Use QuillBot where you write
                    </Typography>

                    <Typography style={{ margin: '0px' }}>
                      Paraphrase on Gmail, Google Docs, Word and where you write
                      on Chrome.
                    </Typography>
                  </Grid>

                  <Box className={classes.extBoxHolder}>
                    <Button
                      className={classes.extBox}
                      onClick={() => {
                        if (!platform.isChromeExtInstalled)
                          selectClickHandler('chrome');
                      }}
                      style={{
                        cursor: platform.isChromeExtInstalled
                          ? 'default'
                          : 'pointer',
                      }}
                      variant="contained"
                      color="primary"
                    >
                      <Img
                        src="/images/theme/commonImages/chrome.png"
                        alt="google chrome icon"
                      />
                      {platform.isChromeExtInstalled ? (
                        <>Installed</>
                      ) : (
                        <>Add to Chrome</>
                      )}
                    </Button>
                    <Button
                      className={classes.extBox}
                      onClick={() => selectClickHandler('docs')}
                      variant="outlined"
                      color="primary"
                    >
                      <Img
                        src="/images/google-docs-icon.svg"
                        alt="google docs icon"
                      />
                      Add to Docs
                    </Button>
                    <Button
                      className={classes.extBox}
                      onClick={() => selectClickHandler('word')}
                      variant="outlined"
                      color="primary"
                    >
                      <Img
                        src="/images/theme/commonImages/word.svg"
                        alt="modes"
                      />
                      Add to Word
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={4}>
                <Img
                  className={classes.quilly}
                  src={`${publicRuntimeConfig.ASSETS_URL}/mascot/welcomeQuilly.png`}
                  alt="claw"
                />{' '}
              </Grid>
            </Grid>

            <Grid
              item
              xs={12}
              md={12}
              sm={12}
              lg={12}
              style={{ flexBasis: 'auto' }}
            >
              <Box
                display="flex"
                alignItems="center"
                style={{ marginTop: '20px' }}
                className={classes.continueBtn}
              >
                <Button
                  variant="text"
                  style={{
                    fontSize: '16px',
                    color: theme.specialColors.BLUE_COLOR,
                  }}
                  onClick={props.onNext}
                >
                  Continue <ChevronRightIcon style={{ marginTop: 3 }} />
                </Button>
              </Box>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}

export default Welcome;
